import { getRange } from '../../helpers/axisDef';
import { renderGrid, renderXaxisNumerical, renderYaxisNumerical } from '../../zoompan-canvas/renderAxes';
import { getBoundData, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTable, VALTYPES } from '../../../../data/interfaces';
import { array2Color, interpolateColors } from '../../helpers/colors';
import { getSelectionHighlightColor, getStandardDataColor } from '../../../themes/themes';
import { renderXLabel, renderYLabel } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';

import { chartDims, generateChartTitle, isSelectedItem } from '../../helpers/misc';
import { getNumericalRangeEnFilterStep } from '../../../../data/selection/selectionEnFilterNumericalRange';
import { numericalValueIsAbsent } from '../../../../components/numerical-edit-with-confirmation/NumericalEditWithConformation';
import { currentTheme } from '../../../themes/themes';

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
) {
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const dataTable: TpDataTable = boundData.dataTable;
  const colInfoValue = boundData.bindings.value.columnInfo;
  const dataValue = boundData.bindings.value.data;
  const dataItemId = boundData.bindings.itemId.data;
  const nameX = boundData.bindings.value.columnInfo.name;
  const nameY = 'Count';
  const title = generateChartTitle(dataContext, dataBinding, config, nameX);

  const [minValData, maxValData] = getRange(dataValue, 0.01);

  const itemCount = dataValue.length;

  const filterInfo = getNumericalRangeEnFilterStep(
    boundData.canonicalTypeInfo.selectionFilter,
    boundData.dataTable.id,
    colInfoValue.id,
  );

  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;

  const resolFactor = config.resolution;
  const estimBinCount = Math.round(Math.sqrt((Math.min(itemCount, 30000) * 2 ** (resolFactor / 2)) / 2 ** (5 / 2)));

  let binSize = config.fixedBinSize;
  if (!binSize) {
    binSize = (maxValData - minValData) / estimBinCount;
  }
  if (colInfoValue.valueType == VALTYPES.INTEGER) binSize = Math.max(binSize, 1);
  const binMin = Math.floor(minValData / binSize) * binSize;
  const binCount = Math.floor((maxValData - binMin) / binSize) + 1;

  let minValPlot = binMin;
  let maxValPlot = binMin + binCount * binSize;
  if (!numericalValueIsAbsent(config.minVal)) minValPlot = config.minVal;
  if (!numericalValueIsAbsent(config.maxVal)) maxValPlot = config.maxVal;
  const rng = maxValPlot - minValPlot;
  minValPlot -= 0.05 * rng;
  maxValPlot += 0.05 * rng;

  const binFrequencies: number[] = [];
  const binFrequenciesSelected: number[] = [];
  for (let i = 0; i < binCount; i++) {
    binFrequencies.push(0);
    binFrequenciesSelected.push(0);
  }

  for (let i = 0; i < itemCount; i++) {
    const binNr = Math.floor((dataValue[i] - binMin) / binSize);
    binFrequencies[binNr]++;
    if (isSelectedItem(selectedItemsMap, dataItemId[i])) binFrequenciesSelected[binNr]++;
  }

  const maxBinFrequency = Math.max(...binFrequencies);

  function renderBins(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;

    renderGrid(renderContext, true, true);

    const colorUnSelected = interpolateColors(
      getStandardDataColor(),
      currentTheme.colors.background,
      0.3,
    ).toCanvasString();
    const colorSelected = getStandardDataColor().toCanvasString();
    ctx.strokeStyle = currentTheme.colors.background.opacity(0.25).toCanvasString();
    ctx.lineWidth = renderContext.pixelRatio * 1;

    for (let binNr = 0; binNr < binCount; binNr++) {
      if (binFrequencies[binNr] > 0) {
        ctx.beginPath();
        ctx.rect(
          mapx(binMin + binNr * binSize),
          mapy(0),
          mapx(binMin + (binNr + 1) * binSize) - mapx(binMin + binNr * binSize),
          mapy(binFrequencies[binNr]) - mapy(0),
        );
        ctx.fillStyle = colorUnSelected;
        ctx.fill();
        ctx.stroke();

        ctx.beginPath();
        ctx.rect(
          mapx(binMin + binNr * binSize),
          mapy(0),
          mapx(binMin + (binNr + 1) * binSize) - mapx(binMin + binNr * binSize),
          mapy(binFrequenciesSelected[binNr]) - mapy(0),
        );
        ctx.fillStyle = colorSelected;
        ctx.fill();
        ctx.stroke();
      }
    }

    if (filterInfo) {
      let renderRangeMin = renderContext.areaXMin;
      if (!isNaN(filterInfo.rangeMin)) renderRangeMin = mapx(filterInfo.rangeMin);
      let renderRangeMax = renderContext.areaXMax;
      if (!isNaN(filterInfo.rangeMax)) renderRangeMax = mapx(filterInfo.rangeMax);

      const grd = ctx.createLinearGradient(
        renderRangeMin,
        renderContext.areaYMin,
        renderRangeMax,
        renderContext.areaYMin,
      );
      if (!isNaN(filterInfo.rangeMin)) grd.addColorStop(0, getSelectionHighlightColor().opacity(0.15).toCanvasString());
      else grd.addColorStop(0.3, getSelectionHighlightColor().opacity(0.03).toCanvasString());
      grd.addColorStop(0.3, getSelectionHighlightColor().opacity(0.03).toCanvasString());
      grd.addColorStop(0.7, getSelectionHighlightColor().opacity(0.03).toCanvasString());
      if (!isNaN(filterInfo.rangeMax)) grd.addColorStop(1, getSelectionHighlightColor().opacity(0.15).toCanvasString());
      else grd.addColorStop(0.3, getSelectionHighlightColor().opacity(0.03).toCanvasString());
      ctx.fillStyle = grd;
      ctx.fillRect(
        renderRangeMin,
        renderContext.areaYMin,
        renderRangeMax - renderRangeMin,
        renderContext.areaYMax - renderContext.areaYMin,
      );
      ctx.strokeStyle = getSelectionHighlightColor().opacity(0.5).toCanvasString();
      ctx.lineWidth = renderContext.pixelRatio * 1;
      ctx.beginPath();
      if (!isNaN(filterInfo.rangeMin)) {
        ctx.moveTo(renderRangeMin, renderContext.areaYMin);
        ctx.lineTo(renderRangeMin, renderContext.areaYMax);
      }
      if (!isNaN(filterInfo.rangeMax)) {
        ctx.moveTo(renderRangeMax, renderContext.areaYMin);
        ctx.lineTo(renderRangeMax, renderContext.areaYMax);
      }
      ctx.stroke();
    }

    if (config.indicatedValues) renderIndicatedValues(renderContext);
  }

  function renderIndicatedValues(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;

    const maskSize = 25 * renderContext.pixelRatio;

    for (const indicatedValue of config.indicatedValues) {
      const xPos = mapx(indicatedValue.value);
      const theColor = array2Color(indicatedValue.color);
      const lineWidth = renderContext.pixelRatio * (indicatedValue.lineWidth ?? 1);
      if (indicatedValue.mask == 'left') {
        ctx.fillStyle = theColor.opacity(0.15).toCanvasString();
        ctx.fillRect(
          xPos - maskSize,
          renderContext.areaYMin,
          maskSize + lineWidth / 2,
          renderContext.areaYMax - renderContext.areaYMin,
        );
      }
      if (indicatedValue.mask == 'right') {
        ctx.fillStyle = theColor.opacity(0.15).toCanvasString();
        ctx.fillRect(
          xPos - lineWidth / 2,
          renderContext.areaYMin,
          maskSize + lineWidth / 2,
          renderContext.areaYMax - renderContext.areaYMin,
        );
      }
      ctx.strokeStyle = theColor.toCanvasString();
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      ctx.moveTo(xPos, renderContext.areaYMin);
      ctx.lineTo(xPos, renderContext.areaYMax);
      ctx.stroke();

      if (indicatedValue.label) {
        ctx.save();
        if (indicatedValue.mask == 'left') {
          ctx.translate(
            xPos + renderContext.mapsize(3) + lineWidth / 2,
            renderContext.areaYMax + renderContext.mapsize(3),
          );
          ctx.textBaseline = 'top';
        } else {
          ctx.translate(
            xPos - renderContext.mapsize(3) - lineWidth / 2,
            renderContext.areaYMax + renderContext.mapsize(3),
          );
          ctx.textBaseline = 'bottom';
        }
        ctx.rotate(-Math.PI / 2);
        renderContext.setFont(9);
        ctx.textAlign = 'right';
        ctx.fillStyle = theColor.toCanvasString();
        ctx.fillText(indicatedValue.label, 0, 0);
        ctx.restore();
      }
    }
  }

  function renderLegend(renderContext: TpAreaRenderContext) {}

  const hasLegend = false;
  const legendBottomSize = hasLegend ? chartDims.legendHeight : 0;

  const padding = chartDims.padding;

  const zpLogicalRangeX = createLogicalRange(minValPlot, maxValPlot);
  const zpLogicalRangeY = createLogicalRange(-0.05 * maxBinFrequency, maxBinFrequency * 1.05);
  const zpRenderRangeX = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.yAxisWidth),
    RenderMaxDiffAbs(padding + 0),
  );
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.headerHeight),
    RenderMaxDiffAbs(padding + chartDims.xAxisHeight + chartDims.xLabelHeight + legendBottomSize),
  );

  const areaPoints: TpChartAreaDefinition = {
    id: 'points',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    canAreaSelect: 'x',
    render: renderBins,
  };

  const chartAreas: TpChartAreaDefinition[] = [
    areaPoints,
    {
      id: 'xaxis',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + chartDims.xAxisHeight + chartDims.xLabelHeight + legendBottomSize),
        RenderMaxDiffAbs(padding + chartDims.xLabelHeight + legendBottomSize),
      ),
      zoomPanX: true,
      render: renderXaxisNumerical,
    },
    {
      id: 'xlabel',
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + chartDims.xLabelHeight + legendBottomSize),
        RenderMaxDiffAbs(padding + legendBottomSize),
      ),
      render: (renderContext: TpAreaRenderContext) => renderXLabel(renderContext, nameX),
    },
    {
      id: 'yaxis',
      logicalRangeX: createLogicalRange(0, 1),
      logicalRangeY: zpLogicalRangeY,
      renderRangeX: createRenderRange(
        RenderMinDiffAbs(padding + 4),
        RenderMinDiffAbs(padding + 4 + chartDims.yAxisWidth),
      ), // offset of Y=4 to make axes overlap
      renderRangeY: zpRenderRangeY,
      zoomPanY: true,
      render: renderYaxisNumerical,
    },
    {
      id: 'ylabel',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + 0),
        RenderMinDiffAbs(padding + chartDims.headerHeight),
      ),
      render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, nameY, title),
    },
  ];

  if (hasLegend) {
    chartAreas.push({
      id: 'legend',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSize), RenderMaxDiffAbs(padding)),
      render: renderLegend,
    });
  }

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
  };

  return {
    definition,
    areaPoints,
    dataTable,
    dataValue,
    nameX,
    nameY,
    itemCount,
    dataItemId,
    colInfoValue,
    filterInfo,
    title,
  };
}
